import { WorkspaceTodoStackScreenProps } from "../WorkspaceTodoStack";

import { useLazyLoadQuery, graphql, useMutation } from "react-relay";
import { WorkspaceTodoDetailScreenQuery } from "./__generated__/WorkspaceTodoDetailScreenQuery.graphql";
import { WorkspaceTodoStoresList } from "./WorkspaceTodoDetailList";

import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { OrgButton } from "../../OrgButton";
import React, { useCallback, useEffect, useState } from "react";
import { useAlert } from "../../../../common/Alert";

import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { useIntl } from "react-intl";
import { useWorkspaceId, useWorkspacePermissions } from "../../workspaceId";
import { HeaderButton } from "../../../../components";
import { WorkspaceTodoDetailScreenHeader } from "./WorkspaceTodoDetailScreenHeader";
import { WorkspaceDetailNavigationHeader } from "../../WorkspaceDetailNavigationHeader";
import { WorkspaceTodoDetailScreenGenerateCSVMutation } from "./__generated__/WorkspaceTodoDetailScreenGenerateCSVMutation.graphql";
import { View } from "react-native";
import { Box } from "arkh";
import { WorkspaceTodoDetailScreenNotifyMutation } from "./__generated__/WorkspaceTodoDetailScreenNotifyMutation.graphql";
import { FormattedDateInWorkspace } from "@/src/common/dates/FormattedDate";
import { useWorkspaceTodoArchiveDescription } from "./useWorkspaceTodoArchiveDescription";
import { WorkspaceScheduledArchiveButton } from "../../WorkspaceTodoSignalShared/WorkspaceScheduledArchiveButton";

export function WorkspaceTodoDetailScreen(
  props: WorkspaceTodoStackScreenProps<"WorkspaceTodoDetail">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => <WorkspaceTodoDetailScreenC {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
}

function openCSV(csvString: string, filename = "export.csv") {
  const blob = new Blob([csvString], { type: "text/csv" });
  const url = URL.createObjectURL(blob);

  // Directly change window location to trigger download
  window.location.href = url;

  // Cleanup after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

function WorkspaceTodoDetailScreenC(
  props: WorkspaceTodoStackScreenProps<"WorkspaceTodoDetail"> & {
    fetchKey: string;
  }
) {
  const workspaceId = useWorkspaceId();

  const data = useLazyLoadQuery<WorkspaceTodoDetailScreenQuery>(
    graphql`
      query WorkspaceTodoDetailScreenQuery($workspaceId: ID!, $todoId: ID!) {
        organisation {
          features
        }
        todoWorkspace(id: $todoId) {
          id
          isWorkspaceOwner(workspaceId: $workspaceId)
          archivedAtDate {
            __typename
          }
          scheduledArchiveAt {
            ...FormattedDate
          }
          status
          responseType
          ...WorkspaceTodoDetailList_todoWorkspace

          ...WorkspaceTodoDetailScreenHeader_todoWorkspace
        }
      }
    `,
    {
      workspaceId,
      todoId: props.route.params.todoWorkspaceId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [generateCSV, generateCSVLoading] =
    useMutation<WorkspaceTodoDetailScreenGenerateCSVMutation>(graphql`
      mutation WorkspaceTodoDetailScreenGenerateCSVMutation(
        $todoWorkspaceId: ID!
      ) {
        todoWorkspace(id: $todoWorkspaceId) {
          generateExportCSV
        }
      }
    `);

  const onGenerateCSV = useCallback(() => {
    generateCSV({
      variables: {
        todoWorkspaceId: data.todoWorkspace!.id,
      },
      onCompleted: (result) => {
        if (result.todoWorkspace.generateExportCSV) {
          openCSV(result.todoWorkspace.generateExportCSV);
        }
      },
    });
  }, [generateCSV, data.todoWorkspace]);

  const [archiveTodo, archiveTodoLoading] = useMutation(graphql`
    mutation WorkspaceTodoDetailScreenArchiveMutation($todoWorkspaceId: ID!) {
      todoWorkspace(id: $todoWorkspaceId) {
        archive {
          id
          archivedAtDate {
            ...FormattedDate
          }
        }
      }
    }
  `);

  const [unarchiveTodo, unarchiveTodoLoading] = useMutation(graphql`
    mutation WorkspaceTodoDetailScreenUnarchiveMutation($todoWorkspaceId: ID!) {
      todoWorkspace(id: $todoWorkspaceId) {
        unarchive {
          id
          archivedAtDate {
            ...FormattedDate
          }
        }
      }
    }
  `);

  const intl = useIntl();
  const archiveTitle = intl.formatMessage({
    defaultMessage: "Archive",
    description: "WorkspaceTodoDetail Archive Title",
  });

  const intl_tokens = useIntlTokens();
  const alert = useAlert();

  const onNavigateToEditScheduledArchiveAt = useCallback(() => {
    props.navigation.navigateDeprecated("WorkspaceTodoEditScheduledArchiveAt", {
      workspaceTodoId: data.todoWorkspace!.id,
      workspaceId,
    });
  }, [data.todoWorkspace, props.navigation, workspaceId]);

  const archiveDescription = useWorkspaceTodoArchiveDescription();
  const onArchive = useCallback(() => {
    return alert.alert(archiveTitle, archiveDescription, [
      {
        text: intl_tokens.Cancel,
        style: "cancel",
      },
      {
        text: intl_tokens.Archive,
        isPreferred: true,
        style: "default",
        onPress: () => {
          archiveTodo({
            variables: {
              todoWorkspaceId: data.todoWorkspace!.id,
            },
            onCompleted: () => {
              props.navigation.replace("WorkspaceTodosArchived", {
                workspaceId,
              });
            },
          });
        },
      },
    ]);
  }, [
    alert,
    archiveDescription,
    archiveTitle,
    archiveTodo,
    data.todoWorkspace,
    intl_tokens.Archive,
    intl_tokens.Cancel,
    props.navigation,
    workspaceId,
  ]);

  const [notifyPending, notifyPendingLoading] =
    useMutation<WorkspaceTodoDetailScreenNotifyMutation>(graphql`
      mutation WorkspaceTodoDetailScreenNotifyMutation($todoWorkspaceId: ID!) {
        todoWorkspace(id: $todoWorkspaceId) {
          triggerNotification {
            todos {
              status
            }
            __typename
          }
        }
      }
    `);
  const Alert = useAlert();

  const notifyPendingTitle = intl.formatMessage({
    defaultMessage: "Notify pending Units",
    description: "WorkspaceTodoDetail Notify Pending Title",
  });

  const onNotifyPending = useCallback(() => {
    Alert.alert(
      notifyPendingTitle,
      intl.formatMessage({
        defaultMessage:
          "You are about to send a notification to all units who have not responded to this todo.",
        description: "WorkspaceTodoDetail Notify Pending Alert Description",
      }),
      [
        {
          style: "cancel",
        },
        {
          style: "default",
          text: notifyPendingTitle,
          onPress() {
            notifyPending({
              onCompleted: (d) => {
                const todos =
                  d.todoWorkspace?.triggerNotification?.todos.filter(
                    (todo) => todo.status === "PENDING"
                  ).length ?? 0;
                Alert.alert(
                  intl.formatMessage({
                    defaultMessage: "Notification Sent",
                    description:
                      "WorkspaceTodoDetail Notify Pending Success Title",
                  }),
                  intl.formatMessage(
                    {
                      defaultMessage:
                        "Notification sent successfully to {count} units",
                      description:
                        "WorkspaceTodoDetail Notify Pending Success Description",
                    },
                    { count: todos }
                  )
                );
              },
              onError: () => {
                Alert.alert(
                  "Notification failed",
                  "Notification failed to send"
                );
              },
              variables: {
                todoWorkspaceId: data.todoWorkspace!.id,
              },
            });
          },
        },
      ]
    );
  }, [Alert, data.todoWorkspace, intl, notifyPending, notifyPendingTitle]);
  useEffect(() => {
    if (!data.todoWorkspace) {
      props.navigation.pop();
    }
  }, [data.todoWorkspace, props.navigation]);

  const { write } = useWorkspacePermissions();

  const [viewType, setViewType] = useState<"list" | "gallery">("list");

  const hasGallery =
    data.todoWorkspace && data.todoWorkspace.responseType === "IMAGES"
      ? true
      : false;

  if (viewType === "gallery" && !hasGallery) {
    setViewType("list");
  }

  if (!data.todoWorkspace) return null;

  const archiveActionsAllowed = data.todoWorkspace.isWorkspaceOwner && write;

  const ListHeaderComponent = (
    <WorkspaceTodoDetailScreenHeader
      todoWorkspace={data.todoWorkspace}
      workspaceId={workspaceId}
      hasProductsFeature={!!data.organisation?.features.includes("PRODUCTS")}
      openProduct={(id) =>
        props.navigation.navigateDeprecated("WorkspaceProduct", {
          id,
        })
      }
    />
  );
  return (
    <React.Fragment>
      <WorkspaceDetailNavigationHeader
        left={
          data.todoWorkspace.archivedAtDate === null ? (
            <HeaderButton
              type="WEB-BACK"
              customLabel={intl.formatMessage({
                defaultMessage: "All Todos",
                description: "WorkspaceTodosOccasion headerBackTitle",
              })}
              onPress={() => {
                props.navigation.navigateDeprecated("WorkspaceTodosMain", {
                  workspaceId,
                });
              }}
            />
          ) : (
            <HeaderButton
              type="WEB-BACK"
              customLabel={intl.formatMessage({
                defaultMessage: "All Archived Todos",
                description: "WorkspaceTodosOccasion archived headerBackTitle",
              })}
              onPress={() => {
                props.navigation.navigateDeprecated("WorkspaceTodosArchived", {
                  workspaceId,
                });
              }}
            />
          )
        }
        right={
          <View style={{ flexDirection: "row" }}>
            {data.todoWorkspace.status === "PUBLISHED" && write ? (
              <Box spaceRight="small">
                <OrgButton
                  backgroundColor="surface"
                  tintColor="text-secondary"
                  title={notifyPendingTitle}
                  icon="notification"
                  loading={notifyPendingLoading}
                  onPress={onNotifyPending}
                  border="border-primary"
                />
              </Box>
            ) : null}
            <Box spaceRight="small">
              <OrgButton
                backgroundColor="surface"
                tintColor="text-secondary"
                title={intl_tokens.Export}
                icon="files"
                loading={generateCSVLoading}
                onPress={onGenerateCSV}
                border="border-primary"
              />
            </Box>
            {archiveActionsAllowed &&
            data.todoWorkspace.scheduledArchiveAt === null &&
            data.todoWorkspace.archivedAtDate === null ? (
              <>
                <Box spaceRight="small">
                  <OrgButton
                    backgroundColor="surface"
                    tintColor="text-secondary"
                    title={intl_tokens.ScheduleArchive}
                    icon="clock"
                    onPress={onNavigateToEditScheduledArchiveAt}
                    border="border-primary"
                  />
                </Box>
                <OrgButton
                  backgroundColor="surface"
                  tintColor="text-secondary"
                  title={intl_tokens.Archive}
                  icon="archive"
                  loading={archiveTodoLoading}
                  onPress={onArchive}
                  border="border-primary"
                />
              </>
            ) : null}
            {archiveActionsAllowed && data.todoWorkspace.archivedAtDate ? (
              <OrgButton
                backgroundColor="surface-primary"
                tintColor="text-secondary"
                title={intl_tokens.Unarchive}
                loading={unarchiveTodoLoading}
                icon="archive"
                onPress={() => {
                  unarchiveTodo({
                    variables: {
                      todoWorkspaceId: data.todoWorkspace!.id,
                    },
                    onCompleted: () => {
                      props.navigation.goBack();
                    },
                  });
                }}
                border="border-primary"
              />
            ) : null}
            {!data.todoWorkspace.archivedAtDate &&
            data.todoWorkspace.scheduledArchiveAt ? (
              <WorkspaceScheduledArchiveButton
                date={
                  <FormattedDateInWorkspace
                    date={data.todoWorkspace.scheduledArchiveAt}
                  />
                }
                disabled={!archiveActionsAllowed}
                onPress={onNavigateToEditScheduledArchiveAt}
              />
            ) : null}
          </View>
        }
      />

      <WorkspaceTodoStoresList
        ListHeaderComponent={ListHeaderComponent}
        todoWorkspace={data.todoWorkspace}
        onPress={(todoId, storeName, timezone) => {
          props.navigation.navigateDeprecated("TodoEdit", {
            todoId,
            storeName,
            storeTimezone: timezone,
          });
        }}
        openProduct={(id) => {
          props.navigation.navigateDeprecated("WorkspaceProduct", {
            id,
          });
        }}
      />
    </React.Fragment>
  );
}
